import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import FadeInSection from '../../components/base/FadeInSection';
import SubjectFilterBar from '../../components/feature/SubjectFilterBar';
import SubjectManagerModal from '../../components/feature/SubjectManagerModal';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import DiaryForm from './components/DiaryForm';
import { useSubjectList } from '../../hooks/useSubjectList';
import { useNavigate } from 'react-router-dom';
import { toggleDiaryLike } from '../../hooks/useNotifications';

interface DiaryEntry {
  id: string;
  user_id: string;
  title: string;
  subject: string;
  content: string;
  study_hours: number | null;
  is_public: boolean;
  tags: string[];
  mood?: string;
  material_url?: string;
  created_at: string;
  updated_at: string;
  user_profiles?: {
    username: string;
    avatar_url?: string;
  };
}

export default function DiaryPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { subjects: subjectTags, refreshSubjects } = useSubjectList();
  const [entries, setEntries] = useState<DiaryEntry[]>([]);
  const [loadingState, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState<DiaryEntry | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [viewMode, setViewMode] = useState<'all' | 'mine'>('all');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [showSubjectManager, setShowSubjectManager] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedMood, setSelectedMood] = useState<string>('');
  const [allTags, setAllTags] = useState<string[]>([]);
  // いいね状態管理
  const [likeCounts, setLikeCounts] = useState<Record<string, number>>({});
  const [likedIds, setLikedIds] = useState<Set<string>>(new Set());
  const [likingIds, setLikingIds] = useState<Set<string>>(new Set());

  const moods = [
    { value: 'great', emoji: '😊', label: '良い' },
    { value: 'normal', emoji: '😐', label: '普通' },
    { value: 'down', emoji: '😔', label: '落ち込み' },
    { value: 'unmotivated', emoji: '😤', label: '無気力' },
    { value: 'motivated', emoji: '💪', label: 'やる気満々' },
  ];

  const getMoodEmoji = (moodValue?: string) => {
    if (!moodValue) return null;
    return moods.find(m => m.value === moodValue)?.emoji || null;
  };

  useEffect(() => {
    if (user) fetchEntries();
  }, [viewMode, user, selectedSubject]);

  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 2500);
      return () => clearTimeout(t);
    }
  }, [toast]);

  // 全タグを収集
  useEffect(() => {
    const tags = new Set<string>();
    entries.forEach(entry => {
      if (entry.tags && Array.isArray(entry.tags)) {
        entry.tags.forEach(tag => tags.add(tag));
      }
    });
    setAllTags(Array.from(tags).sort());
  }, [entries]);

  // いいね数・自分のいいね状態を一括取得
  const fetchLikes = useCallback(async (entryIds: string[]) => {
    if (entryIds.length === 0) return;
    try {
      // いいね数を取得
      const { data: likesData } = await supabase
        .from('diary_likes')
        .select('diary_entry_id')
        .in('diary_entry_id', entryIds);

      const counts: Record<string, number> = {};
      entryIds.forEach(id => { counts[id] = 0; });
      (likesData || []).forEach((l: { diary_entry_id: string }) => {
        counts[l.diary_entry_id] = (counts[l.diary_entry_id] || 0) + 1;
      });
      setLikeCounts(counts);

      // 自分のいいね状態
      if (user) {
        const { data: myLikes } = await supabase
          .from('diary_likes')
          .select('diary_entry_id')
          .in('diary_entry_id', entryIds)
          .eq('user_id', user.id);
        const myLikedSet = new Set<string>((myLikes || []).map((l: { diary_entry_id: string }) => l.diary_entry_id));
        setLikedIds(myLikedSet);
      }
    } catch (e) {
      console.error('いいね取得エラー:', e);
    }
  }, [user]);

  // ローディング中
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-600 dark:text-gray-400">読み込み中...</p>
        </div>
      </div>
    );
  }

  // 未ログイン時のガード表示
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
        <Header />
        <section className="pt-20 sm:pt-24 pb-16 hero-gradient-animated hero-dark-force relative overflow-hidden flex-1 flex flex-col">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-20 -right-20 w-[420px] h-[420px] bg-white/10 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-32 -left-20 w-[500px] h-[500px] bg-emerald-300/10 rounded-full blur-3xl"></div>
            <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '32px 32px' }}></div>
          </div>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 relative flex-1 flex flex-col justify-center py-12">
            <div className="text-center mb-10">
              <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-6 border border-white/30">
                <i className="ri-book-2-line text-4xl text-white"></i>
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">学習日記</h1>
              <p className="text-lg text-teal-100">日々の学習記録と振り返りを記録します</p>
            </div>

            <div className="grid md:grid-cols-3 gap-4 sm:gap-6 mb-10">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-edit-line text-2xl text-white"></i>
                </div>
                <h3 className="text-base font-semibold text-white mb-2">日記作成</h3>
                <p className="text-sm text-teal-100">学習内容や気づきを自由に記録</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-emotion-line text-2xl text-white"></i>
                </div>
                <h3 className="text-base font-semibold text-white mb-2">気分記録</h3>
                <p className="text-sm text-teal-100">学習時の気分を記録して分析</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-calendar-line text-2xl text-white"></i>
                </div>
                <h3 className="text-base font-semibold text-white mb-2">履歴管理</h3>
                <p className="text-sm text-teal-100">過去の日記を振り返り、成長を実感</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/login')}
                className="px-8 py-3 bg-white text-teal-700 rounded-xl font-bold hover:bg-teal-50 transition-colors whitespace-nowrap shadow-lg"
              >
                <i className="ri-login-box-line mr-2"></i>ログイン
              </button>
              <button
                onClick={() => navigate('/login')}
                className="px-8 py-3 bg-white/15 text-white border border-white/30 rounded-xl font-bold hover:bg-white/25 transition-colors whitespace-nowrap backdrop-blur-sm"
              >
                <i className="ri-user-add-line mr-2"></i>新規登録
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  const fetchEntries = async () => {
    setLoading(true);
    let query = supabase
      .from('diary_entries')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (viewMode === 'mine' && user) {
      query = query.eq('user_id', user.id);
    } else if (viewMode === 'all') {
      query = query.eq('is_public', true);
    }
    
    if (selectedSubject) {
      query = query.eq('subject', selectedSubject);
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error('Error fetching diary entries:', error);
      setEntries([]);
      setLoading(false);
      return;
    }

    const entriesData = data || [];

    // user_profilesを別クエリで取得してマージ
    if (entriesData.length > 0) {
      const userIds = [...new Set(entriesData.map((e: DiaryEntry) => e.user_id))];
      const { data: profiles } = await supabase
        .from('user_profiles')
        .select('user_id, username, avatar_url')
        .in('user_id', userIds);

      const profileMap: Record<string, { username: string; avatar_url?: string }> = {};
      (profiles || []).forEach((p: { user_id: string; username: string; avatar_url?: string }) => {
        profileMap[p.user_id] = { username: p.username, avatar_url: p.avatar_url };
      });

      const merged = entriesData.map((e: DiaryEntry) => ({
        ...e,
        user_profiles: profileMap[e.user_id] || undefined,
      }));
      setEntries(merged);
      // いいね情報を取得
      await fetchLikes(merged.map((e: DiaryEntry) => e.id));
    } else {
      setEntries([]);
    }
    
    setLoading(false);
  };

  const handleLike = async (e: React.MouseEvent, entry: DiaryEntry) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user || likingIds.has(entry.id)) return;
    setLikingIds(prev => new Set(prev).add(entry.id));
    try {
      const result = await toggleDiaryLike(entry.id, entry.title, entry.user_id, user.id, '');
      setLikeCounts(prev => ({ ...prev, [entry.id]: result.count }));
      setLikedIds(prev => {
        const next = new Set(prev);
        if (result.liked) next.add(entry.id); else next.delete(entry.id);
        return next;
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLikingIds(prev => { const next = new Set(prev); next.delete(entry.id); return next; });
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('この日記を削除してもよろしいですか？')) return;

    try {
      const { error } = await supabase.from('diary_entries').delete().eq('id', id).eq('user_id', user!.id);
      if (error) throw error;
      setToast({ message: '日記を削除しました', type: 'success' });
      fetchEntries();
    } catch (error) {
      console.error('Error deleting diary entry:', error);
      setToast({ message: '削除に失敗しました', type: 'error' });
    }
  };

  const handleEdit = (entry: DiaryEntry) => {
    setEditingEntry(entry);
    setShowForm(true);
  };

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const isContentLong = (content: string) => {
    return content.length > 150;
  };

  const toggleTag = (tag: string) => {
    setSelectedTags(prev => {
      if (prev.includes(tag)) {
        return prev.filter(t => t !== tag);
      } else {
        return [...prev, tag];
      }
    });
  };

  const toggleMood = (mood: string) => {
    setSelectedMood(prev => prev === mood ? '' : mood);
  };

  const clearAllFilters = () => {
    setSearchQuery('');
    setSelectedTags([]);
    setSelectedMood('');
  };

  const hasActiveFilters = searchQuery.trim() !== '' || selectedTags.length > 0 || selectedMood !== '';

  const filteredEntries = entries.filter(entry => {
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const matchesText = entry.content?.toLowerCase().includes(query) ||
                         entry.title?.toLowerCase().includes(query);
      if (!matchesText) return false;
    }

    if (selectedTags.length > 0) {
      const entryTags = entry.tags || [];
      const hasAllTags = selectedTags.every(tag => entryTags.includes(tag));
      if (!hasAllTags) return false;
    }

    if (selectedMood) {
      if (entry.mood !== selectedMood) return false;
    }

    return true;
  });

  const isMyEntry = (entry: DiaryEntry) => {
    if (!user) return false;
    return entry.user_id === user.id;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <Header />

      {toast && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[60] animate-fade-in">
          <div className={`px-5 py-3 rounded-xl shadow-lg text-sm font-medium flex items-center gap-2 ${toast.type === 'success' ? 'bg-gradient-to-r from-teal-500 to-emerald-600 text-white' : 'bg-red-500 text-white'}`}>
            <i className={`${toast.type === 'success' ? 'ri-checkbox-circle-fill' : 'ri-error-warning-fill'} text-base`}></i>
            {toast.message}
          </div>
        </div>
      )}

      <section className="pt-24 pb-12 hero-gradient-animated hero-dark-force relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="hero-orb-1 absolute -top-20 -right-20 w-[420px] h-[420px] bg-white/10 rounded-full blur-3xl"></div>
          <div className="hero-orb-2 absolute -bottom-32 -left-20 w-[500px] h-[500px] bg-emerald-300/10 rounded-full blur-3xl"></div>
          <div className="hero-orb-3 absolute top-1/3 left-1/3 w-[350px] h-[350px] bg-teal-200/8 rounded-full blur-3xl"></div>
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '32px 32px' }}></div>
          <div className="absolute -top-40 -right-40 w-[800px] h-[200px] bg-gradient-to-r from-transparent via-white/5 to-transparent rotate-[30deg] blur-2xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
          <nav className="flex items-center gap-2 text-sm text-teal-200 mb-6 animate-hero-fade-up">
            <Link to="/" className="hover:text-white cursor-pointer transition-colors">Top</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white font-medium">勉強日記</span>
          </nav>
          <div className="flex items-end justify-between gap-4 flex-wrap animate-hero-fade-up-delay">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white mb-3 tracking-tight">勉強日記</h1>
              <p className="text-teal-100 text-sm sm:text-base max-w-2xl leading-relaxed">
                日々の学習記録を振り返り、成長を実感しましょう。
              </p>
            </div>
            <div className="flex items-center gap-2 animate-hero-fade-up-delay2">
              <button
                onClick={() => setShowSubjectManager(true)}
                className="flex items-center gap-2 px-4 sm:px-5 py-2.5 bg-white/15 text-white rounded-xl font-medium hover:bg-white/25 transition-all whitespace-nowrap cursor-pointer backdrop-blur-sm border border-white/10 shadow-sm"
              >
                <i className="ri-folder-settings-line text-base"></i>
                <span className="hidden sm:inline">科目・カテゴリ管理</span>
                <span className="sm:hidden">管理</span>
              </button>
              {user && (
                <button
                  onClick={() => { setEditingEntry(null); setShowForm(true); }}
                  className="flex items-center gap-2 px-5 py-2.5 bg-white/15 text-white rounded-xl font-medium hover:bg-white/25 transition-all whitespace-nowrap cursor-pointer backdrop-blur-sm border border-white/10 shadow-sm"
                >
                  <i className="ri-add-line text-lg"></i>
                  新規作成
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {user && (
        <FadeInSection>
          <section className="py-4 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
            <div className="max-w-7xl mx-auto px-4 sm:px-6">
              <div className="inline-flex bg-gray-100 dark:bg-gray-700 rounded-full p-1">
                <button
                  onClick={() => setViewMode('all')}
                  className={`px-5 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${viewMode === 'all' ? 'bg-gradient-to-r from-teal-500 to-emerald-600 text-white shadow-sm' : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white'}`}
                >
                  <i className="ri-global-line mr-1.5"></i>全体
                </button>
                <button
                  onClick={() => setViewMode('mine')}
                  className={`px-5 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${viewMode === 'mine' ? 'bg-gradient-to-r from-teal-500 to-emerald-600 text-white shadow-sm' : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white'}`}
                >
                  <i className="ri-user-line mr-1.5"></i>自分だけ
                </button>
              </div>
            </div>
          </section>
        </FadeInSection>
      )}

      <FadeInSection>
        <section className="py-6 sm:py-8 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <SubjectFilterBar
                    subjects={subjectTags}
                    selectedSubject={selectedSubject}
                    onSelectSubject={setSelectedSubject}
                    onRefresh={refreshSubjects}
                  />
                </div>
                <div className="relative flex-1 max-w-md">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                  <input
                    type="text"
                    placeholder="日記を検索..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <i className="ri-emotion-line text-gray-600 dark:text-gray-400"></i>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">気分で絞り込み</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {moods.map(mood => (
                    <button
                      key={mood.value}
                      onClick={() => toggleMood(mood.value)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                        selectedMood === mood.value
                          ? 'bg-gradient-to-r from-teal-500 to-emerald-600 text-white shadow-sm'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      <span className="text-base">{mood.emoji}</span>
                      <span>{mood.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {allTags.length > 0 && (
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <i className="ri-price-tag-3-line text-gray-600 dark:text-gray-400"></i>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">タグで絞り込み</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {allTags.map(tag => (
                      <button
                        key={tag}
                        onClick={() => toggleTag(tag)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                          selectedTags.includes(tag)
                            ? 'bg-gradient-to-r from-teal-500 to-emerald-600 text-white shadow-sm'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {hasActiveFilters && (
                <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-400">適用中のフィルター:</span>
                  
                  {searchQuery.trim() && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 text-xs font-medium rounded-full">
                      <i className="ri-search-line"></i>
                      検索: {searchQuery}
                      <button onClick={() => setSearchQuery('')} className="ml-1 hover:text-teal-900 dark:hover:text-teal-200 cursor-pointer">
                        <i className="ri-close-line"></i>
                      </button>
                    </span>
                  )}

                  {selectedMood && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 text-xs font-medium rounded-full">
                      <span>{getMoodEmoji(selectedMood)}</span>
                      {moods.find(m => m.value === selectedMood)?.label}
                      <button onClick={() => setSelectedMood('')} className="ml-1 hover:text-teal-900 dark:hover:text-teal-200 cursor-pointer">
                        <i className="ri-close-line"></i>
                      </button>
                    </span>
                  )}

                  {selectedTags.map(tag => (
                    <span key={tag} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 text-xs font-medium rounded-full">
                      <i className="ri-price-tag-3-line"></i>
                      {tag}
                      <button onClick={() => toggleTag(tag)} className="ml-1 hover:text-teal-900 dark:hover:text-teal-200 cursor-pointer">
                        <i className="ri-close-line"></i>
                      </button>
                    </span>
                  ))}

                  <button
                    onClick={clearAllFilters}
                    className="ml-auto px-3 py-1 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-close-circle-line mr-1"></i>
                    すべてクリア
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>
      </FadeInSection>

      <FadeInSection>
        <section className="py-10 sm:py-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            {loadingState ? (
              <div className="text-center py-16">
                <div className="w-10 h-10 border-3 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
              </div>
            ) : filteredEntries.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-24 h-24 flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-full mx-auto mb-6">
                  <i className="ri-file-list-3-line text-4xl text-gray-400 dark:text-gray-500"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-2">
                  {hasActiveFilters ? '条件に一致する日記が見つかりません' : (viewMode === 'mine' ? 'まだ自分の日記がありません' : 'まだ日記がありません')}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {hasActiveFilters ? 'フィルター条件を変更してみてください。' : (viewMode === 'mine' ? '「新規作成」ボタンから最初の日記を書いてみましょう。' : '日記が投稿されると、ここに表示されます。')}
                </p>
                {hasActiveFilters && (
                  <button
                    onClick={clearAllFilters}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-teal-500 to-emerald-600 text-white rounded-xl font-medium hover:from-teal-600 hover:to-emerald-700 transition-colors cursor-pointer whitespace-nowrap shadow-md shadow-teal-200/40"
                  >
                    <i className="ri-close-circle-line"></i>フィルターをクリア
                  </button>
                )}
                {!hasActiveFilters && viewMode === 'mine' && user && (
                  <button
                    onClick={() => { setEditingEntry(null); setShowForm(true); }}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-teal-500 to-emerald-600 text-white rounded-xl font-medium hover:from-teal-600 hover:to-emerald-700 transition-colors cursor-pointer whitespace-nowrap shadow-md shadow-teal-200/40"
                  >
                    <i className="ri-add-line"></i>最初の日記を作成
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredEntries.map((entry) => {
                  const isExpanded = expandedIds.has(entry.id);
                  const showToggle = isContentLong(entry.content);
                  const isLiked = likedIds.has(entry.id);
                  const likeCount = likeCounts[entry.id] || 0;
                  const isLiking = likingIds.has(entry.id);
                  
                  return (
                    <div key={entry.id} className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 shadow-sm flex flex-col">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2 flex-wrap">
                          {entry.subject && (
                            <span className="px-2.5 py-0.5 bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 text-xs font-medium rounded-full">
                              {entry.subject}
                            </span>
                          )}
                          {entry.mood && (
                            <span className="text-xl" title="気分">
                              {getMoodEmoji(entry.mood)}
                            </span>
                          )}
                          {isMyEntry(entry) && (
                            <span className="px-1.5 py-0.5 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 text-xs font-medium rounded-full">
                              自分
                            </span>
                          )}
                          {entry.material_url && (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 text-xs font-medium rounded-full border border-amber-200 dark:border-amber-700"
                              title="教材リンクあり"
                            >
                              <i className="ri-shopping-bag-3-line text-xs"></i>
                              教材あり
                            </span>
                          )}
                        </div>
                        {isMyEntry(entry) && viewMode === 'mine' && (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEdit(entry)}
                              className="p-1.5 text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/30 rounded-lg transition-colors cursor-pointer"
                              title="編集"
                            >
                              <i className="ri-edit-line text-sm"></i>
                            </button>
                            <button
                              onClick={() => handleDelete(entry.id)}
                              className="p-1.5 text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors cursor-pointer"
                              title="削除"
                            >
                              <i className="ri-delete-bin-line text-sm"></i>
                            </button>
                          </div>
                        )}
                      </div>
                      
                      {entry.user_profiles && (
                        <div className="flex items-center gap-2 mb-3">
                          {entry.user_profiles.avatar_url ? (
                            <img
                              src={entry.user_profiles.avatar_url}
                              alt={entry.user_profiles.username}
                              className="w-8 h-8 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-400 to-emerald-400 flex items-center justify-center text-white text-xs font-semibold">
                              {entry.user_profiles.username.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            {entry.user_profiles.username}
                          </span>
                        </div>
                      )}
                      
                      <Link to={`/diary/${entry.id}`}>
                        <h3 className="text-base font-semibold text-gray-800 dark:text-white mb-2 line-clamp-2 hover:text-teal-600 dark:hover:text-teal-400 transition-colors cursor-pointer">
                          {entry.title}
                        </h3>
                      </Link>
                      <p className={`text-sm text-gray-600 dark:text-gray-400 mb-3 leading-relaxed ${isExpanded ? '' : 'line-clamp-3'}`}>
                        {entry.content}
                      </p>
                      
                      {entry.material_url && (
                        <a
                          href={entry.material_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="mt-2 mb-3 flex items-center gap-2 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg text-xs text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
                        >
                          <i className="ri-shopping-cart-line flex-shrink-0"></i>
                          <span className="truncate">教材リンクを見る</span>
                          <i className="ri-external-link-line flex-shrink-0 ml-auto"></i>
                        </a>
                      )}
                      
                      {showToggle && (
                        <button
                          onClick={() => toggleExpand(entry.id)}
                          className="text-xs text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 font-medium mb-3 flex items-center gap-1 cursor-pointer transition-colors"
                        >
                          {isExpanded ? (
                            <><span>折りたたむ</span><i className="ri-arrow-up-s-line text-sm"></i></>
                          ) : (
                            <><span>続きを読む</span><i className="ri-arrow-down-s-line text-sm"></i></>
                          )}
                        </button>
                      )}
                      
                      {/* カード下部：日付・学習時間・いいね */}
                      <div className="mt-auto flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-700">
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {new Date(entry.created_at).toLocaleDateString('ja-JP')}
                          </span>
                          {entry.study_hours !== null && (
                            <span className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1">
                              <i className="ri-time-line"></i>
                              {entry.study_hours}時間
                            </span>
                          )}
                        </div>
                        {/* いいねボタン */}
                        <button
                          onClick={(e) => handleLike(e, entry)}
                          disabled={!user || isLiking}
                          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer disabled:opacity-50 ${
                            isLiked
                              ? 'bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400'
                              : 'bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-500 dark:hover:text-red-400'
                          }`}
                          title={user ? (isLiked ? 'いいねを取り消す' : 'いいねする') : 'ログインが必要です'}
                        >
                          <i className={`${isLiked ? 'ri-heart-fill' : 'ri-heart-line'} text-sm`}></i>
                          <span>{likeCount}</span>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </FadeInSection>

      {showForm && user && (
        <DiaryForm
          userId={user.id}
          entry={editingEntry}
          onClose={() => {
            setShowForm(false);
            setEditingEntry(null);
          }}
          onSaved={() => {
            setShowForm(false);
            setEditingEntry(null);
            setToast({ message: editingEntry ? '日記を更新しました' : '日記を作成しました', type: 'success' });
            fetchEntries();
            refreshSubjects();
          }}
        />
      )}

      {showSubjectManager && (
        <SubjectManagerModal
          onClose={() => setShowSubjectManager(false)}
          onUpdated={() => {
            refreshSubjects();
          }}
        />
      )}

      <Footer />
    </div>
  );
}
